import "@local/css/tailwind.css";
import "@local/sass/main.scss";
import { register as registerMenus } from "@local/ts/interactions/MenuInteractions";

import { domReady, domSelectorReady } from "@local/ts/lib/Utility";
import * as DeltaScroll from "@local/ts/interactions/DeltaScrollInteractions";
import * as ElementToggleInteractions from "@local/ts/interactions/ElementToggleInteractions";
import * as CarouselInteractions from "@local/ts/interactions/CarouselInteractions";
window["ElementToggleInteractions"] = ElementToggleInteractions;
import * as StateProfessionDropdownInteractions from "@local/ts/interactions/StateProfessionDropdownInteractions";
import * as FilterFormSubmitInteractions from "@local/ts/interactions/FilterFormSubmitInteractions";
import * as SearchStateProfessionDropdownInteractions from "@local/ts/interactions/SearchStateProfessionDropdownInteractions";
import * as CartInteractions from "@local/ts/interactions/CartInteractions";

const registerLightboxWhenNeeded = async (): Promise<void> => {
  if (!document.querySelector(".glightbox")) {
    return;
  }

  const [{ default: GLightbox }, GLightboxInteractions] = await Promise.all([
    import("glightbox"),
    import("@local/ts/interactions/GLightboxInteractions"),
  ]);

  window.GLightbox = GLightbox;
  GLightboxInteractions.register();
};

const registerAccordionWhenNeeded = async (): Promise<void> => {
  if (!document.querySelector('[data-svelte="Accordion"]')) {
    return;
  }

  const [{ svelteDOM }, { default: Accordion }] = await Promise.all([
    import("@local/ts/lib/svelte-dom-loader"),
    import("@local/svelte/FaqAccordion.svelte"),
  ]);

  svelteDOM.registerComponents([
    {
      component: Accordion,
      name: "Accordion",
      props: { name: "typescript" },
    },
  ]);
};

const registerInputMasksWhenNeeded = async (): Promise<void> => {
  if (!document.querySelector("[data-input-mask]")) {
    return;
  }

  const InputMaskInteractions = await import(
    "@local/ts/interactions/InputMaskInteractions"
  );
  InputMaskInteractions.mask();
};

const registerCartWhenNeeded = (): void => {
  // Marketing pages such as the BuilderCE homepage do not render product/cart
  // action controls. Keep cart behavior out of the runtime path there, while
  // registering immediately on product pages so CartInteractions can observe
  // the normal pageshow event and resolve server-rendered processing buttons.
  if (!document.querySelector("[data-cart-action]")) {
    return;
  }

  CartInteractions.register();
};

const registerDeltaScroll = (): void => {
  DeltaScroll.register({
    dead_zone: 50,
    minimum_movement: 30,
    below_threshold: 60,
    small_width: 1024,
    small_height: 915,
  });
};

const registerDeltaScrollWhenAppropriate = (): void => {
  if (!document.querySelector(".builderce-home")) {
    registerDeltaScroll();
    return;
  }

  // DeltaScroll reads viewport geometry and writes body data attributes. Those
  // measurements are useful after the homepage becomes interactive, but they
  // should not force layout during the first paint/LCP window.
  const schedule = () => {
    const run = () => registerDeltaScroll();
    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(run, { timeout: 1500 });
    } else {
      window.setTimeout(run, 250);
    }
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }
};

const logRegistrationFailures = (
  label: string,
  results: PromiseSettledResult<unknown>[],
): void => {
  results.forEach((result, index) => {
    if (result.status === "rejected") {
      // A lazy enhancement must never prevent unrelated controls from being
      // initialized. Keep the failure visible to developers without turning it
      // into an unhandled rejection that aborts the application bootstrap.
      console.warn(`${label} registration ${index + 1} failed`, result.reason);
    }
  });
};

(async () => {
  await domReady();

  // CartInteractions historically waits for pageshow so browser back/forward
  // restores refresh cart state. Register it before any optional asynchronous
  // enhancements; otherwise a lazy import can arrive after pageshow and leave
  // the server-rendered cart button permanently in its processing state.
  registerMenus();
  registerCartWhenNeeded();

  // Optional/lazy enhancements settle independently so a missing chunk cannot
  // suppress later core interactions.
  const optionalResults = await Promise.allSettled([
    registerLightboxWhenNeeded(),
    registerAccordionWhenNeeded(),
    registerInputMasksWhenNeeded(),
  ]);
  logRegistrationFailures("Optional UI", optionalResults);

  registerDeltaScrollWhenAppropriate();
  ElementToggleInteractions.register();
  CarouselInteractions.register();
  FilterFormSubmitInteractions.register();
  SearchStateProfessionDropdownInteractions.register();

  const asyncCoreResults = await Promise.allSettled([
    StateProfessionDropdownInteractions.register(),
  ]);
  logRegistrationFailures("Async UI", asyncCoreResults);

  const body = await domSelectorReady("body");
  body[0].classList.remove("disable-animations");
})();

console.log("App loaded");